import { useState, useEffect } from "react";
import { useAuth } from "@/auth-provider";
import { GlassPanel } from "@/components/ui/glass-panel";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { useLocation } from "@/components/location-provider";

export default function NotificationsPage() {
  const { user } = useAuth();
  const { current: location } = useLocation();
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined" && "Notification" in window) {
      setPermission(Notification.permission);
    }
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg) {
      const sub = await reg.pushManager.getSubscription();
      if (sub) setSubscribed(true);
    }
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);

      if (perm !== "granted") {
        setLoading(false);
        return;
      }

      const reg = await navigator.serviceWorker.register("/sw.js");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: import.meta.env.VITE_VAPID_PUBLIC_KEY,
      });

      await fetch("/api/push", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sub.toJSON()),
      });

      setSubscribed(true);
    } catch (e) {
      console.error("Push subscription failed:", e);
    }
    setLoading(false);
  };

  const handleUnsubscribe = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          const endpoint = sub.endpoint;
          await sub.unsubscribe();
          await fetch(`/api/push?endpoint=${encodeURIComponent(endpoint)}`, { method: "DELETE" });
        }
      }
      setSubscribed(false);
    } catch (e) {
      console.error("Unsubscribe failed:", e);
    }
    setLoading(false);
  };

  return (
    <div className="space-y-5 animate-slide-up">
      <PageHeader
        eyebrow="Settings"
        title="Notifications"
        subtitle={`${location.name}, ${location.country}`}
      />

      {!user ? (
        <GlassPanel intensity="low" className="p-12 text-center">
          <Bell className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-lg font-bold">Sign In Required</h2>
          <p className="text-sm text-muted-foreground mt-1">Create an account to enable weather notifications.</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={() => window.location.href = "/login"}>
            Sign In
          </Button>
        </GlassPanel>
      ) : (
        <div className="space-y-4">
          <GlassPanel intensity="medium" className="p-5">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 shrink-0">
                {subscribed ? (
                  <Bell className="h-5 w-5 text-primary" />
                ) : (
                  <BellOff className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg">
                  {subscribed ? "Notifications Enabled" : "Enable Notifications"}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {subscribed
                    ? "You'll receive alerts for severe weather, forecast changes, and custom thresholds."
                    : "Get push notifications for severe weather alerts and custom thresholds in your area."}
                </p>
                {permission === "denied" && (
                  <p className="text-xs text-destructive mt-2">
                    Notifications are blocked in your browser. Please enable them in your browser settings.
                  </p>
                )}
                <div className="mt-4">
                  {subscribed ? (
                    <Button variant="outline" size="sm" onClick={handleUnsubscribe} disabled={loading}>
                      {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
                      Disable Notifications
                    </Button>
                  ) : (
                    <Button size="sm" onClick={handleSubscribe} disabled={loading || permission === "denied"}>
                      {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Bell className="h-3.5 w-3.5 mr-1.5" />}
                      Enable Notifications
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </GlassPanel>

          {subscribed && (
            <>
              <GlassPanel intensity="low" className="p-5">
                <h3 className="text-sm font-semibold mb-3">Alert Types</h3>
                <div className="space-y-3">
                  {[
                    { label: "Severe weather alerts", desc: "Tornadoes, hurricanes, extreme conditions", defaultChecked: true },
                    { label: "Weather warnings", desc: "Winter storms, flooding, high wind", defaultChecked: true },
                    { label: "Weather watches", desc: "Potential hazardous conditions developing", defaultChecked: false },
                    { label: "Daily forecast", desc: "Morning summary of today's weather", defaultChecked: false },
                    { label: "UV index alerts", desc: "When UV exceeds your threshold", defaultChecked: false },
                  ].map((item) => (
                    <label key={item.label} className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        defaultChecked={item.defaultChecked}
                        className="mt-1 h-4 w-4 rounded border-border/50 text-primary focus:ring-primary/50"
                      />
                      <div>
                        <span className="text-sm font-medium group-hover:text-foreground transition-colors">{item.label}</span>
                        <p className="text-xs text-muted-foreground">{item.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </GlassPanel>

              <GlassPanel intensity="low" className="p-5">
                <h3 className="text-sm font-semibold mb-3">Custom Thresholds</h3>
                <p className="text-xs text-muted-foreground mb-4">
                  Get notified when conditions exceed your limits.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">UV Index threshold</label>
                    <input
                      type="range"
                      min={1}
                      max={12}
                      defaultValue={8}
                      className="w-full mt-1 accent-primary"
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground">
                      <span>1 (Low)</span>
                      <span>12 (Extreme)</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Wind speed threshold (km/h)</label>
                    <input
                      type="range"
                      min={20}
                      max={120}
                      defaultValue={50}
                      className="w-full mt-1 accent-primary"
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground">
                      <span>20 km/h</span>
                      <span>120 km/h</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Temperature threshold (°C)</label>
                    <input
                      type="range"
                      min={-30}
                      max={50}
                      defaultValue={35}
                      className="w-full mt-1 accent-primary"
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground">
                      <span>-30°C</span>
                      <span>50°C</span>
                    </div>
                  </div>
                </div>
              </GlassPanel>
            </>
          )}
        </div>
      )}
    </div>
  );
}